/** Which parts of an error a log or telemetry sink may emit. One policy for every sink. */

import { NON_ERROR_CAUSE_PLACEHOLDER } from './constants.js';

/** An `Error`, a `DOMException` or a cross-realm error. */
export type ErrorLike = { name: string; message: string };

type ErrorField = string | number | boolean;

/** The parts of an error that a sink may emit. */
export interface EmittableError {
  name: string;
  message: string;
  stack: string | undefined;
  /** Primitive own properties, such as `code` or `status`. */
  fields: [string, ErrorField][];
  /** An error cause, or a placeholder for any other cause. */
  cause: ErrorLike | typeof NON_ERROR_CAUSE_PLACEHOLDER | undefined;
}

/**
 * Error names whose message is built from the caller's input. Prisma prints the whole
 * query, argument values included, into a validation error's message, whatever the
 * client's `errorFormat`.
 */
const INPUT_ECHOING_ERROR_NAMES = new Set(['PrismaClientValidationError']);

const WITHHELD_ERROR_MESSAGE = '[message withheld: it repeats the input]';

const NON_ERROR_THROWN_MESSAGE = 'A non-Error value was thrown';

/** Duck-typed error check: covers `Error` plus `DOMException` and cross-realm errors. */
export function isErrorLike(value: unknown): value is ErrorLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    'name' in value &&
    'message' in value &&
    typeof value.name === 'string' &&
    typeof value.message === 'string'
  );
}

/** The message a sink may emit for an error with this name. */
export function emittableErrorMessage(name: string, message: string): string {
  return INPUT_ECHOING_ERROR_NAMES.has(name) ? WITHHELD_ERROR_MESSAGE : message;
}

/**
 * Reduce an error to what a sink may emit. Libraries hang upstream data off error
 * objects — openid-client puts the parsed token-endpoint body on `cause`, fetch clients
 * attach the `Response`, Prisma attaches `meta` — so only primitive own properties and
 * an error `cause` are kept.
 */
export function emittableError(error: ErrorLike): EmittableError {
  const message = emittableErrorMessage(error.name, error.message);
  const rawStack = error instanceof Error ? error.stack : undefined;
  const stack =
    rawStack === undefined || message === error.message
      ? rawStack
      : rawStack.replace(error.message, message);

  const fields: [string, ErrorField][] = [];
  for (const [key, value] of Object.entries(error)) {
    if (key === 'name' || key === 'message' || key === 'stack' || key === 'cause') {
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      fields.push([key, value]);
    }
  }

  const rawCause: unknown = 'cause' in error ? error.cause : undefined;
  const cause =
    rawCause === undefined
      ? undefined
      : isErrorLike(rawCause)
        ? rawCause
        : NON_ERROR_CAUSE_PLACEHOLDER;

  return { name: error.name, message, stack, fields, cause };
}

/**
 * Any caught value as an `Error`. A thrown non-Error becomes an Error with a fixed
 * message: `String()` of an arbitrary thrown value can carry a response body or user
 * input. The logger applies this to the `error` key itself; call it where a caught value
 * goes somewhere else, such as an `onError` handler or `captureException`.
 */
export function asError(value: unknown): Error {
  return value instanceof Error ? value : new Error(NON_ERROR_THROWN_MESSAGE);
}
